import {
  createContext,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useContext,
  useMemo
} from 'react';
import { Navigate, Route, useLocation, useParams } from 'react-router-dom';
import { DISPLAY, PROGRAMMATIC } from '@blisspointmedia/bpm-types/dist/ScpChannels';
import { Role, RoleType } from '@blisspointmedia/bpm-types/dist/Users';
import { CustomDashboard, customDashboardSlug } from '@Pages/CustomDashboard';
import { ProtectedPage } from '@Components/ProtectedPage';
import { useAppSelector } from '../../redux';
import { CustomDashboardInfo } from '../../redux/company';
import {
  emailSelector,
  isInternalSelector,
  roleSelector,
  userExperimentsSelector
} from '../../redux/user';
import { useCompanyInfo } from '../hooks/useCompanyInfo';
import { useLocationCompany } from '../hooks/useLocationCompany';
import {
  FullBpmLocation,
  FullNavGroup,
  FullSubNavContainer,
  FullTertiaryNavContainer,
  FullTertiaryNavSection,
  INTERNAL_TOOLS_TERTIARY_KEY,
  LocationType,
  NavGroup,
  NavItem,
  SubNavItem,
  SubNavLocation
} from './location-constants';
import { NAV_LOCATIONS } from './locations';
import {
  hasLegacyPresetSuffix,
  parseLegacyPresetSuffix,
  setPresetSearchParams
} from './preset-url-utils';

const buildCustomDashboardSubNavs = (customDashboards: CustomDashboardInfo[]): SubNavLocation[] =>
  customDashboards.map(({ name }) => ({
    key: customDashboardSlug(name),
    prettyName: name,
    pageComponent: CustomDashboard,
    type: LocationType.Client
  }));

// The 'custom' nav item's subNavs are static in NAV_LOCATIONS (empty) — real content is a
// function of the current company's custom_dashboards, so it's generated here per company
// rather than baked into the location tree.
const withDynamicCustomDashboards = (
  navGroups: NavGroup[],
  customDashboards: CustomDashboardInfo[]
): NavGroup[] =>
  navGroups.map(group => ({
    ...group,
    items: group.items.map(item =>
      item.key === 'custom' && 'subNavs' in item
        ? { ...item, subNavs: buildCustomDashboardSubNavs(customDashboards) }
        : item
    )
  }));

interface NavLocations {
  readonly isReady: boolean;
  readonly fullLocations: FullNavGroup[];
  readonly flatLocations: FullBpmLocation[];
  readonly clientFlatLocations: FullBpmLocation[];
  readonly globalFlatLocations: FullBpmLocation[];
  readonly clientRoutes: ReactNode;
  readonly globalRoutes: ReactNode;
}

const DEFAULT_NAV_LOCATIONS: NavLocations = {
  isReady: false,
  fullLocations: [],
  flatLocations: [],
  clientFlatLocations: [],
  globalFlatLocations: [],
  clientRoutes: undefined,
  globalRoutes: undefined
};

const NavLocationsContext = createContext<NavLocations | null>(null);

export const NavLocationsProvider = ({ children }: PropsWithChildren) => {
  const value = useNavLocationsImpl();
  return <NavLocationsContext.Provider value={value}>{children}</NavLocationsContext.Provider>;
};

export const useNavLocations = (): NavLocations => {
  const ctx = useContext(NavLocationsContext);
  if (!ctx) throw new Error('useNavLocations must be used within NavLocationsProvider');
  return ctx;
};

// Ported from bpm-frontend NavBar: TTD Programmatic replaces Display for companies on
// enableUseTTDProgramatic, unless hidePublicTTDProgramatic is on without enableProgrammaticPages.
export const withProgrammaticMediaType = (
  mediaTypes: string[] | undefined,
  experiments: Set<string>
): string[] | undefined => {
  if (
    !mediaTypes ||
    !experiments.has('enableUseTTDProgramatic') ||
    (experiments.has('hidePublicTTDProgramatic') && !experiments.has('enableProgrammaticPages'))
  ) {
    return mediaTypes;
  }
  return [...mediaTypes.filter(mt => mt !== DISPLAY && mt !== PROGRAMMATIC), PROGRAMMATIC];
};

const useNavLocationsImpl = (): NavLocations => {
  const userEmail = useAppSelector(emailSelector);
  const userRole = useAppSelector(roleSelector);
  const isInternal = useAppSelector(isInternalSelector);
  const locationCid = useLocationCompany();
  const {
    cid: companyInfoCid,
    experiments: pgCompanyExperimentsArr,
    media_types: companyMediaTypes,
    custom_dashboards: companyCustomDashboards,
    pacing_client_facing: companyPacingClientFacing,
    dtc_mmm_run: companyDtcMmmRun,
    commerce_mmm_run: companyCommerceMmmRun
  } = useCompanyInfo();
  const pgUserExperimentsArr = useAppSelector(userExperimentsSelector);
  const pgUserExperiments = useMemo(
    () => new Set<string>(pgUserExperimentsArr ?? []),
    [pgUserExperimentsArr]
  );
  const pgCompanyExperiments = useMemo(
    () => new Set<string>(pgCompanyExperimentsArr ?? []),
    [pgCompanyExperimentsArr]
  );

  // null = companies not yet loaded; '' = global route (no company needed)
  const isCompanyInfoFetched =
    locationCid !== null &&
    (locationCid === '' || (Boolean(companyInfoCid) && locationCid === companyInfoCid));

  const isReady =
    Boolean(userEmail) &&
    userRole !== undefined &&
    pgUserExperiments !== null &&
    isCompanyInfoFetched;

  // Client routes and location data depend on both experiment sets.
  // When no client is selected (locationCid === ''), skip company experiment filtering so that
  // company-experiment-gated routes still appear in search — useBpmNavigate will prompt for a client.
  const clientData = useMemo(() => {
    if (!isReady) return null;
    const noCompany = locationCid === '';
    const baseNavGroups = withDynamicCustomDashboards(NAV_LOCATIONS, companyCustomDashboards);
    const resolvedMediaTypes = noCompany
      ? undefined
      : withProgrammaticMediaType(
          companyMediaTypes,
          new Set([...pgUserExperiments, ...pgCompanyExperiments])
        );
    const customDashboardsLength = companyCustomDashboards?.length ?? 0;
    const isTestCompany = locationCid === 'tinuititest';
    const fullLocations = buildFullLocations(
      filterNavLocations(
        baseNavGroups,
        pgUserExperiments,
        pgCompanyExperiments,
        userRole,
        noCompany,
        resolvedMediaTypes,
        customDashboardsLength,
        isInternal,
        companyPacingClientFacing,
        isTestCompany,
        Boolean(companyDtcMmmRun),
        Boolean(companyCommerceMmmRun)
      )
    );
    // Routes keep nav-only-experiment items so their pages stay reachable by URL, and resolve
    // mediaType by inheriting down from each ancestor (pushDownMediaTypes) so a leaf can declare
    // its own broader mediaType than its parent without that broader value leaking to siblings.
    const routeLocations = buildFullLocations(
      filterNavLocations(
        pushDownMediaTypes(baseNavGroups),
        pgUserExperiments,
        pgCompanyExperiments,
        userRole,
        noCompany,
        resolvedMediaTypes,
        customDashboardsLength,
        isInternal,
        companyPacingClientFacing,
        isTestCompany,
        Boolean(companyDtcMmmRun),
        Boolean(companyCommerceMmmRun),
        true
      )
    );
    const flatLocations = flattenLocations(fullLocations);
    const clientFlatLocations = flattenLocations(fullLocations, LocationType.Client);
    const globalFlatLocations = flattenLocations(fullLocations, LocationType.Global);
    const clientRoutes = [
      ...buildChannelBareRedirectRoutes(routeLocations),
      buildRoutes(flattenLocations(routeLocations, LocationType.Client))
    ];
    return { fullLocations, flatLocations, clientFlatLocations, globalFlatLocations, clientRoutes };
  }, [
    isReady,
    pgCompanyExperiments,
    pgUserExperiments,
    userRole,
    locationCid,
    companyMediaTypes,
    companyCustomDashboards,
    isInternal,
    companyPacingClientFacing,
    companyDtcMmmRun,
    companyCommerceMmmRun
  ]);

  // Global routes are unaffected by company experiments — stable across company switches
  const globalRoutes = useMemo(() => {
    if (!isReady) return undefined;
    const filteredLocations = filterNavLocations(
      NAV_LOCATIONS,
      pgUserExperiments,
      new Set<string>(),
      userRole
    );
    const fullLocations = buildFullLocations(filteredLocations);
    return buildRoutes(flattenLocations(fullLocations, LocationType.Global));
  }, [isReady, pgUserExperiments, userRole]);

  return useMemo(() => {
    if (!clientData) return DEFAULT_NAV_LOCATIONS;
    return { isReady, ...clientData, globalRoutes };
  }, [isReady, clientData, globalRoutes]);
};

const joinPath = (...segments: string[]) => `/${segments.filter(Boolean).join('/')}`;

const isRoleAllowed = (roleRequired: RoleType | undefined, userRole: number): boolean => {
  if (roleRequired === undefined) return true;

  return userRole <= roleRequired;
};

// Route-building only: makes a container's `mediaType` the inherited default for any
// descendant that doesn't declare its own — a leaf's own `mediaType` (e.g. the shared
// 'creatives'/'status' pages nested under 'streaming' that also serve Display/Audio-only
// companies) always wins over what it would otherwise inherit. The container's own field is
// then cleared so `allowed()` recurses into it instead of short-circuiting on the container's
// (now-irrelevant) own value; the existing "drop an empty container" rules in
// `filterNavLocations` prune it once none of its descendants survive. A no-op everywhere no
// descendant declares a broader `mediaType` than its ancestor. A container with
// `mediaTypeGatesNavOnly` passes nothing down, so its routes stay open to every company.
export const pushDownMediaTypes = (navGroups: NavGroup[]): NavGroup[] =>
  navGroups.map(group => ({
    ...group,
    items: group.items.map((item): NavItem => {
      if (!('subNavs' in item)) return item;
      const inherited = item.mediaTypeGatesNavOnly ? undefined : item.mediaType;
      return {
        ...item,
        mediaType: undefined,
        subNavs: item.subNavs.map((subNav): SubNavItem => {
          const resolvedSubNav = { ...subNav, mediaType: subNav.mediaType ?? inherited };
          if (!('tertiaryNavs' in resolvedSubNav)) return resolvedSubNav;
          const subInherited = resolvedSubNav.mediaType;
          return {
            ...resolvedSubNav,
            mediaType: undefined,
            tertiaryNavs: resolvedSubNav.tertiaryNavs.map(section => ({
              ...section,
              navs: section.navs.map(nav => ({ ...nav, mediaType: nav.mediaType ?? subInherited }))
            }))
          };
        })
      };
    })
  }));

export const filterNavLocations = (
  navGroups: NavGroup[],
  pgUserExperiments: Set<string>,
  pgCompanyExperiments: Set<string>,
  userRole: number,
  skipCompanyExperimentFilter = false,
  mediaTypes?: string[],
  customDashboardsLength = 0,
  isInternal = false,
  pacingClientFacing = false,
  isTestCompany = false,
  hasDtcMmmRun = false,
  hasCommerceMmmRun = false,
  treatNavOnlyExperimentsAsEnabled = false
): NavGroup[] => {
  // Normalize legacy 'tv' API value to match the LINEAR constant 'linear'
  const normalizedMediaTypes = mediaTypes?.map(mt => (mt === 'tv' ? 'linear' : mt));

  const allowed = (item: {
    experiment?: string;
    companyExperiment?: string;
    experimentGatesNavOnly?: boolean;
    experimentExternalOnly?: boolean;
    roleRequired?: RoleType;
    mediaType?: string | string[];
    showWhenCustomDashboards?: boolean;
    showWhenPacingClientFacing?: boolean;
    showWhenDtcMmmRun?: boolean;
    showWhenCommerceMmmRun?: boolean;
    hideFromNav?: boolean;
  }): boolean => {
    if (!isRoleAllowed(item.roleRequired, userRole)) return false;
    const navOnlyBypass = treatNavOnlyExperimentsAsEnabled && item.experimentGatesNavOnly;
    const internalBypass = isInternal && item.experimentExternalOnly;
    // Company-scoped flags are unknown until a client is selected (they come from
    // /companies, not /me), so when no client is selected we let the item through
    // to keep company-only experiments searchable — mirrors `companyExperiment` below.
    if (item.experiment && !navOnlyBypass && !internalBypass && !skipCompanyExperimentFilter) {
      if (!pgUserExperiments.has(item.experiment) && !pgCompanyExperiments.has(item.experiment)) {
        return false;
      }
    }
    // Company-scoped second flag: AND'd with `experiment`. Checked against the
    // company experiment set (the tinuititest company is exempt). Skipped when no
    // client is selected so the route stays searchable (mirrors the check above).
    if (item.companyExperiment && !navOnlyBypass && !skipCompanyExperimentFilter) {
      if (!pgCompanyExperiments.has(item.companyExperiment) && !isTestCompany) {
        return false;
      }
    }
    // Custom dashboards check intentionally ignores skipCompanyExperimentFilter —
    // this channel is never shown without dashboards, even when no client is selected.
    if (item.showWhenCustomDashboards) {
      return customDashboardsLength > 0;
    }
    // Internal users always; external users only when the company's pacing_client_facing
    // flag is set. Like showWhenCustomDashboards above, this intentionally ignores
    // skipCompanyExperimentFilter so the gate also applies to the route build — otherwise an
    // external user at a non-client-facing company could still reach the page by direct URL
    // (the backend only checks company access, not this flag).
    if (item.showWhenPacingClientFacing) {
      return isInternal || pacingClientFacing;
    }
    // Nav-only gates (mirrors experimentGatesNavOnly's navOnlyBypass): hidden from the
    // sidebar until the company has a matching mmm_job_dates run, but the route always
    // renders during the route-build pass so a direct URL still works either way.
    // Test companies always show them: their run flags come from a per-page proxy company's
    // mmm_job_dates, so the entries would otherwise flicker as the user navigates.
    if (item.showWhenDtcMmmRun) {
      return treatNavOnlyExperimentsAsEnabled || isTestCompany || hasDtcMmmRun;
    }
    if (item.showWhenCommerceMmmRun) {
      return treatNavOnlyExperimentsAsEnabled || isTestCompany || hasCommerceMmmRun;
    }
    // Unconditionally removed from the sidebar; still routed during the route-build pass.
    if (item.hideFromNav) {
      return treatNavOnlyExperimentsAsEnabled;
    }
    if (item.mediaType && normalizedMediaTypes) {
      const requiredMediaTypes = Array.isArray(item.mediaType) ? item.mediaType : [item.mediaType];
      return requiredMediaTypes.some(mt => normalizedMediaTypes.includes(mt));
    }
    return true;
  };

  return navGroups
    .map(group => ({
      ...group,
      items: group.items
        .filter(allowed)
        .map(item => {
          if (!('subNavs' in item)) return item;
          return {
            ...item,
            subNavs: item.subNavs
              .filter(allowed)
              .map(subNav => {
                if (!('tertiaryNavs' in subNav)) return subNav;
                return {
                  ...subNav,
                  tertiaryNavs: subNav.tertiaryNavs
                    .map(section => ({ ...section, navs: section.navs.filter(allowed) }))
                    .filter(section => section.navs.length > 0)
                };
              })
              .filter(subNav => !('tertiaryNavs' in subNav) || subNav.tertiaryNavs.length > 0)
          };
        })
        .filter(item => !('subNavs' in item) || item.subNavs.length > 0)
    }))
    .filter(group => group.items.length > 0);
};

/*
  Expands NavGroups to add additional properties to children based on their parent's properties
  1. Each BpmLocation gains a fullPath: string; key which combines the keys of it and all its parents to create a full URI path for routing
  2. Each BpmLocation gains a searchNames: string[] key which contains the prettyNames of it and all its parents for use when searching
  3. Each TertiaryNavContainer gains a parentIcon which is equal to it's parent's icon
*/
const buildFullLocations = (navGroups: NavGroup[]): FullNavGroup[] =>
  navGroups.map(group => ({
    ...group,
    items: group.items.map(item => {
      const fullPath = item.fullPath ?? joinPath(item.key);
      const searchNames = [group.prettyName, item.prettyName].filter(s => Boolean(s));
      if (!('subNavs' in item)) {
        return {
          ...item,
          fullPath,
          searchNames
        };
      }

      return {
        ...item,
        fullPath,
        searchNames,
        subNavs: item.subNavs?.map(subNav => {
          if (!('tertiaryNavs' in subNav)) {
            return {
              ...subNav,
              fullPath: subNav.fullPath ?? joinPath(item.key, subNav.key),
              searchNames: [...searchNames, subNav.prettyName].filter(s => Boolean(s))
            };
          }

          return {
            ...subNav,
            parentIcon: item.icon,
            tertiaryNavs: subNav.tertiaryNavs.map(section => ({
              ...section,
              navs: section.navs.map(nav => ({
                ...nav,
                type: section.type,
                fullPath:
                  nav.fullPath ??
                  (subNav.key === INTERNAL_TOOLS_TERTIARY_KEY
                    ? joinPath(item.key, nav.key)
                    : joinPath(item.key, subNav.key, nav.key)),
                searchNames: [...searchNames, subNav.prettyName, nav.prettyName].filter(s =>
                  Boolean(s)
                )
              }))
            }))
          };
        })
      };
    })
  }));

const stripWildcard = (fullPath: string): string => fullPath.replace(/\/\*$/, '');

export const withSearchTabs = (location: FullBpmLocation): FullBpmLocation[] => [
  location,
  ...(location.searchTabs ?? []).map(tab => ({
    ...location,
    key: `${location.key}/${tab.key}`,
    prettyName: tab.prettyName,
    fullPath: `${stripWildcard(location.fullPath)}/${tab.key}`,
    searchNames: [...location.searchNames, tab.prettyName],
    searchTabs: undefined
  }))
];

// Keeps the first entry per fullPath, so pass the locations matching the current context first.
// Paths can also repeat within one list when a page has several gated entries.
export const buildSearchLocations = (
  preferred: FullBpmLocation[],
  other: FullBpmLocation[]
): FullBpmLocation[] => {
  const seenPaths = new Set<string>();
  return [...preferred, ...other].flatMap(withSearchTabs).filter(loc => {
    if (seenPaths.has(loc.fullPath)) {
      return false;
    }
    seenPaths.add(loc.fullPath);
    return true;
  });
};

const flattenTertiaryNav = ({ navs, type }: FullTertiaryNavSection): FullBpmLocation[] =>
  navs.map(nav => ({ ...nav, type }));

const flattenSubNav = (subNav: FullBpmLocation | FullTertiaryNavContainer): FullBpmLocation[] => {
  if (!('tertiaryNavs' in subNav)) return [subNav];
  return subNav.tertiaryNavs.flatMap(flattenTertiaryNav);
};

const flattenNavGroupItem = (item: FullBpmLocation | FullSubNavContainer): FullBpmLocation[] => {
  if (!('subNavs' in item)) return [item];
  return item.subNavs.flatMap(flattenSubNav);
};

const flattenLocations = (
  fullLocations: FullNavGroup[],
  filterType?: LocationType
): FullBpmLocation[] => {
  const allLocations = fullLocations.flatMap(({ items }) => items.flatMap(flattenNavGroupItem));
  if (!filterType) return allLocations;
  return allLocations.filter(({ type }) => type === filterType);
};

// Alias routes preserve the company and any subpath when redirecting to the canonical URL.
// The bare group route owns no wildcard, so its `*` is the outer router's whole remaining URL
// rather than a subpath; `keepSuffix={false}` drops it. The query string and hash carry over.
const RedirectToLocation = ({
  to,
  keepSuffix = true,
  retiredFrom
}: {
  to: string;
  keepSuffix?: boolean;
  retiredFrom?: string;
}) => {
  const { company, '*': suffix } = useParams();
  const { search, hash } = useLocation();
  const base = company ? `/${company}${to}` : to;
  const pathname = keepSuffix && suffix ? `${base}/${suffix}` : base;
  return (
    <Navigate
      to={{ pathname, search, hash }}
      state={retiredFrom ? { retiredFrom } : undefined}
      replace
    />
  );
};

// bpm-frontend put the tab and the preset in one path segment with no separator
// (`<channel>/overview_page-preset=Default_table-preset=Platform`); hand-edited links also drop the
// tab (`<channel>/_page-preset=Default`). Send both to the tab's query-string form
// (`<channel>/overview?preset=Default&table=Platform`); any other single segment goes to the
// default tab. `tabs` maps each tab's key to its canonical path.
const ChannelLegacyPresetRedirect = ({
  defaultTab,
  tabs
}: {
  defaultTab: string;
  tabs: Record<string, string>;
}) => {
  const { company, legacyTabAndPreset = '' } = useParams();
  const { search } = useLocation();
  const isTabLess = hasLegacyPresetSuffix(legacyTabAndPreset);
  const tab = isTabLess
    ? defaultTab
    : Object.keys(tabs).find(
        key =>
          legacyTabAndPreset.startsWith(key) &&
          hasLegacyPresetSuffix(legacyTabAndPreset.slice(key.length))
      );
  if (!tab) return <Navigate to={`/${company}${tabs[defaultTab]}`} replace />;

  const presetSearch = setPresetSearchParams(
    search,
    parseLegacyPresetSuffix(legacyTabAndPreset.slice(isTabLess ? 0 : tab.length))
  );
  return <Navigate to={`/${company}${tabs[tab]}${presetSearch}`} replace />;
};

// Channel group nodes (social, streaming, display, etc.) have subNavs but no route of their own —
// buildRoutes only emits routes for flattened leaf locations, so the bare group path (e.g.
// /company/social) previously fell through to NotFound. Redirect it to the channel's default
// Overview subpage instead.
export const buildChannelBareRedirectRoutes = (fullLocations: FullNavGroup[]): ReactElement[] =>
  fullLocations.flatMap(({ items }) =>
    items.flatMap(item => {
      if (!('subNavs' in item)) return [];
      const overviewSubNav = item.subNavs.find(
        subNav => !('tertiaryNavs' in subNav) && subNav.key === 'overview'
      ) as FullBpmLocation | undefined;
      if (!overviewSubNav) return [];
      const tabs = Object.fromEntries(
        item.subNavs
          .filter(subNav => !('tertiaryNavs' in subNav))
          .map(subNav => [subNav.key, (subNav as FullBpmLocation).fullPath])
      );
      // Same spellings buildRoutes aliases for leaf pages (cross-channel, crosschannel, cross_channel).
      const groupPath = item.fullPath.slice(1);
      const spellings = [
        ...new Set([groupPath, groupPath.replace(/-/g, ''), groupPath.replace(/-/g, '_')])
      ];
      return spellings.flatMap(spelling => [
        <Route
          key={`${spelling}-bare-redirect`}
          path={spelling}
          element={<RedirectToLocation to={overviewSubNav.fullPath} keepSuffix={false} />}
        />,
        <Route
          key={`${spelling}-legacy-preset-redirect`}
          path={`${spelling}/:legacyTabAndPreset`}
          element={<ChannelLegacyPresetRedirect defaultTab='overview' tabs={tabs} />}
        />
      ]);
    })
  );

export const buildRoutes = (locations: FullBpmLocation[]): ReactNode =>
  locations.flatMap(
    ({
      pageComponent,
      key,
      fullPath,
      roleRequired = Role.EXTERNAL,
      redirects = [],
      retiredRedirects = [],
      channel
    }) => {
      const canonicalPath = stripWildcard(fullPath);
      const relativePath = canonicalPath.slice(1);
      const allRedirects = [
        ...redirects,
        relativePath.replace(/-/g, ''),
        relativePath.replace(/-/g, '_')
      ].filter(redirect => redirect !== relativePath);

      const element = (
        <ProtectedPage
          suspense
          component={pageComponent}
          roleRequired={roleRequired}
          channel={channel}
        />
      );
      // Each page and alias owns its wildcard, including an empty one at the page root.
      // A bare route would inherit the outer data router's wildcard containing the entire URL.
      return [
        <Route key={key} path={`${relativePath}/*`} element={element} />,
        ...allRedirects.map(redirect => (
          <Route
            key={`${key}-${redirect}`}
            path={`${redirect}/*`}
            element={<RedirectToLocation to={canonicalPath} />}
          />
        )),
        ...retiredRedirects.map(redirect => (
          <Route
            key={`${key}-${redirect}`}
            path={`${redirect}/*`}
            element={<RedirectToLocation to={canonicalPath} retiredFrom={redirect} />}
          />
        ))
      ];
    }
  );
